import { useEffect, useMemo, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Button, Alert, alpha, CircularProgress } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { COLORS } from '../../theme/themeConstants';
import { useSettlementsApi } from '../../Hooks/useSettlementsAPI';
import { hasHashInUrl, getUrlInfo } from '../../utils/hashUrlUtils';

const SettlementHyperPaySuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifySettlementPayment, loading } = useSettlementsApi();
  const [resultMessage, setResultMessage] = useState<string>('');
  const [success, setSuccess] = useState<boolean | null>(null);
  const hasVerified = useRef(false);

  // Check if hash is present in URL and log URL information
  const urlHasHash = hasHashInUrl();
  const urlDebugInfo = getUrlInfo();
  
  console.log('=== Settlement Payment Success Page ===');
  console.log('Has hash (#) present?', urlHasHash);
  console.log('Full URL info:', urlDebugInfo);
  console.log('Search params:', Object.fromEntries(searchParams.entries()));
  
  // Debug localStorage
  console.log('LocalStorage hyperpay data:', {
    checkoutId: localStorage.getItem('hyperpay_checkout_id'),
    merchantId: localStorage.getItem('hyperpay_merchant_id'),
    settlementPaymentId: localStorage.getItem('hyperpay_settlement_payment_id')
  });

  const status = useMemo(() => searchParams.get('status') || '', [searchParams]);
  const settlementPaymentId = useMemo(() => searchParams.get('settlementPaymentId') || '', [searchParams]);
  const checkoutId = useMemo(() => searchParams.get('id') || searchParams.get('checkoutId') || '', [searchParams]);
  const merchantId = useMemo(() => searchParams.get('merchantId') || '', [searchParams]);
  const message = useMemo(() => searchParams.get('message') || '', [searchParams]);

  useEffect(() => {
    if (hasVerified.current) return;
    
    // If we have a status from URL params, use it directly
    if (status) {
      hasVerified.current = true;
      setSuccess(status === 'success');
      setResultMessage(message || (status === 'success' ? 'Settlement payment completed successfully!' : 'Payment verification failed'));
      return;
    }

    // Otherwise, verify payment if we have the required parameters
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const currentMerchantId = merchantId || userData?.userId;
    
    if (!currentMerchantId || !checkoutId || !settlementPaymentId) {
      console.error('Missing required parameters for verification:', { 
        merchantId: currentMerchantId, 
        checkoutId, 
        settlementPaymentId 
      });
      setSuccess(false);
      setResultMessage('Missing payment information. Please contact support.');
      return;
    }

    const verifyPayment = async () => {
      try {
        console.log('Verifying settlement payment with:', { checkoutId, merchantId: currentMerchantId, settlementPaymentId });
        
        const response = await verifySettlementPayment({ 
          checkoutId: String(checkoutId), 
          merchantId: String(currentMerchantId),
          settlementPaymentId: String(settlementPaymentId)
        });
        
        setSuccess(Boolean(response.data.success));
        setResultMessage(response.data.message || (response.data.success ? 'Settlement payment completed successfully!' : 'Payment verification failed'));
        
        // Clear localStorage after successful verification
        if (response.data.success) {
          localStorage.removeItem('hyperpay_checkout_id');
          localStorage.removeItem('hyperpay_merchant_id');
          localStorage.removeItem('hyperpay_settlement_payment_id');
        }
      } catch (error) {
        console.error('Payment verification error:', error);
        setSuccess(false);
        setResultMessage('Payment verification failed. Please contact support if you were charged.');
      }
    };

    verifyPayment();
    hasVerified.current = true;
  }, [status, checkoutId, merchantId, settlementPaymentId, message, verifySettlementPayment]);

  const getStatusIcon = () => {
    if (success === true) {
      return <CheckCircleOutlineIcon sx={{ fontSize: 80, color: '#4caf50' }} />;
    } else if (success === false) {
      return <ErrorOutlineIcon sx={{ fontSize: 80, color: '#f44336' }} />;
    }
    return <CircularProgress size={80} sx={{ color: COLORS.primary.main }} />;
  };

  const getStatusTitle = () => {
    if (success === true) return 'Payment Successful!';
    if (success === false) return 'Payment Failed';
    return 'Verifying Payment...';
  };

  const getStatusColor = () => {
    if (success === true) return '#4caf50';
    if (success === false) return '#f44336';
    return COLORS.primary.main;
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2,
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eaed 100%)'
    }}>
      <Paper sx={{
        p: 4,
        maxWidth: 560,
        width: '100%',
        border: '1px solid #e8eaed',
        borderRadius: 3,
        textAlign: 'center'
      }}>
        {/* Status Icon */}
        <Box sx={{ mb: 3 }}>
          {getStatusIcon()}
        </Box>

        {/* Title */}
        <Typography variant="h5" sx={{ 
          fontWeight: 700, 
          mb: 1,
          color: getStatusColor()
        }}>
          {getStatusTitle()}
        </Typography>

        {/* Settlement Payment ID */}
        {settlementPaymentId && (
          <Box sx={{
            mb: 3,
            p: 2,
            backgroundColor: alpha(COLORS.primary.main, 0.05),
            borderRadius: 2,
            border: `1px solid ${alpha(COLORS.primary.main, 0.2)}`
          }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Settlement Payment ID: {settlementPaymentId}
            </Typography>
          </Box>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center', mb: 3 }}>
            <CircularProgress size={22} />
            <Typography variant="body2">Verifying your payment...</Typography>
          </Box>
        )}

        {/* Result Message */}
        {!loading && resultMessage && (
          <Alert 
            severity={success ? 'success' : 'error'} 
            sx={{ mb: 3, textAlign: 'left' }}
          >
            {resultMessage}
          </Alert>
        )}

        {/* Success Message */}
        {success === true && (
          <Box sx={{
            mb: 3,
            p: 2,
            backgroundColor: alpha('#4caf50', 0.1),
            borderRadius: 2,
            border: `1px solid ${alpha('#4caf50', 0.3)}`
          }}>
            <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500 }}>
              <strong>✔ Settlement payment completed successfully!</strong>
            </Typography>
            <Typography variant="body2" sx={{ color: '#2e7d32', mt: 1 }}>
              Your settlement has been processed and updated in your account.
            </Typography>
          </Box>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button 
            variant="contained" 
            onClick={() => navigate('/layout/settlements')}
            sx={{
              backgroundColor: COLORS.primary.main,
              color: 'white',
              px: 3,
              py: 1,
              borderRadius: 2,
              fontWeight: 'bold',
              textTransform: 'uppercase',
              '&:hover': {
                backgroundColor: COLORS.primary.dark,
              }
            }}
          >
            Back to Settlements
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/layout/dashboard')}
            sx={{
              borderColor: COLORS.primary.main,
              color: COLORS.primary.main,
              px: 3,
              py: 1,
              borderRadius: 2,
              fontWeight: 'bold',
              textTransform: 'uppercase',
              '&:hover': {
                borderColor: COLORS.primary.dark,
                backgroundColor: alpha(COLORS.primary.main, 0.04)
              }
            }}
          >
            Go to Dashboard
          </Button>
        </Box>

        {/* Debug Info (only in development) */}
        {import.meta.env.DEV && (
          <Box sx={{ mt: 4, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, fontSize: '12px' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Debug Info:<br />
              Status: {status}<br />
              Checkout ID: {checkoutId}<br />
              Settlement Payment ID: {settlementPaymentId}<br />
              Merchant ID: {merchantId}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default SettlementHyperPaySuccess;
